/**
 * F5.6 — OSRM public router client.
 *
 * Uses the open public instance at router.project-osrm.org. No API key,
 * no auth, rate-limit ≈ best-effort. If we ever hit limits in prod we
 * self-host on a $4/mo VPS (see card F5.6 risks).
 *
 * Coordinate order in OSRM is **lon,lat** (not lat,lon — gotcha!).
 */

export interface OsrmStop {
  istat: string;
  name: string;
  lat: number;
  lon: number;
}

export interface OsrmLeg {
  duration: number; // seconds
  distance: number; // meters
}

export interface OsrmTrip {
  /** stops in OSRM-optimized order */
  ordered: OsrmStop[];
  totalDuration: number; // seconds
  totalDistance: number; // meters
  /** OSRM-encoded polyline of the whole route (precision 5, polyline algorithm) */
  geometryPolyline: string;
  legs: OsrmLeg[];
}

interface OsrmTripResponse {
  code: string;
  trips: Array<{
    duration: number;
    distance: number;
    geometry: string;
    legs: Array<{ duration: number; distance: number }>;
  }>;
  waypoints: Array<{
    waypoint_index: number; // position in OPTIMAL order
    location: [number, number]; // [lon, lat]
  }>;
}

const OSRM_BASE = 'https://router.project-osrm.org';

export async function planTrip(stops: OsrmStop[]): Promise<OsrmTrip> {
  if (stops.length < 2) {
    throw new Error('Visit planner needs at least 2 stops.');
  }
  if (stops.length > 7) {
    throw new Error('Visit planner supports up to 7 stops at a time.');
  }
  const coords = stops.map((s) => `${s.lon},${s.lat}`).join(';');
  // source=first / destination=last / roundtrip=false would force endpoints,
  // but for "visit a few places in any order" we let OSRM pick freely.
  const url = `${OSRM_BASE}/trip/v1/driving/${coords}?roundtrip=false&source=any&destination=any&geometries=polyline&overview=full`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`OSRM error: HTTP ${res.status}`);
  }
  const data = (await res.json()) as OsrmTripResponse;
  if (data.code !== 'Ok' || !data.trips.length) {
    throw new Error(`OSRM declined: ${data.code}`);
  }
  const trip = data.trips[0];
  const order = new Array<OsrmStop>(stops.length);
  for (let inputIdx = 0; inputIdx < data.waypoints.length; inputIdx++) {
    const wp = data.waypoints[inputIdx];
    order[wp.waypoint_index] = stops[inputIdx];
  }
  return {
    ordered: order.filter(Boolean),
    totalDuration: trip.duration,
    totalDistance: trip.distance,
    geometryPolyline: trip.geometry,
    legs: trip.legs,
  };
}

/** Haversine distance in km between two lat/lon points. */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Google Maps deep link for the planned trip. */
export function googleMapsUrl(trip: OsrmTrip): string {
  // https://developers.google.com/maps/documentation/urls/get-started#directions-action
  const wps = trip.ordered.map((s) => `${s.lat},${s.lon}`).join('|');
  const origin = trip.ordered[0];
  const destination = trip.ordered[trip.ordered.length - 1];
  const intermediate = trip.ordered.slice(1, -1).map((s) => `${s.lat},${s.lon}`).join('|');
  const params = new URLSearchParams({
    api: '1',
    origin: `${origin.lat},${origin.lon}`,
    destination: `${destination.lat},${destination.lon}`,
    travelmode: 'driving',
  });
  if (intermediate) params.set('waypoints', intermediate);
  // Keep wps in fallback for legacy clients
  void wps;
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Apple Maps deep link to navigate to a single stop from current location. */
export function appleMapsNavUrl(stop: OsrmStop): string {
  return `https://maps.apple.com/?daddr=${stop.lat},${stop.lon}&t=m`;
}

/** Google Maps deep link to navigate to a single stop from current location. */
export function googleMapsNavUrl(stop: OsrmStop): string {
  const params = new URLSearchParams({
    api: '1',
    destination: `${stop.lat},${stop.lon}`,
    travelmode: 'driving',
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** True when running on an Apple device (iPhone, iPad, or Mac). */
export function isAppleDevice(): boolean {
  return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
}

/** Format seconds as "1h 24m" / "37m". */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

/** Format meters as "126 km" / "950 m". */
export function formatDistance(meters: number): string {
  if (meters >= 1000) return `${(meters / 1000).toFixed(meters >= 10000 ? 0 : 1)} km`;
  return `${Math.round(meters)} m`;
}
